import React, { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AttachmentList, Select, FileUpload, Drawer, toast } from "@outpave/ds";
import { useDisplayAttachments } from "hooks/useDisplayAttachments";
import { toAttachmentItems } from "utils/attachments/attachmentItems";
import {
  useAttachmentListLabels,
  useAttachmentViewerLabels,
} from "hooks/useAttachmentLabels";
import {
  LocalImageAsset,
  useAttachmentAndAssetHandling,
  OcrInvoiceResult,
} from "hooks/useAttachmentAndAssetHandling";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { toFileList } from "utils/receipts/receiptUpload";
import {
  ATTACHMENT_FILE_ACCEPT,
  partitionAttachmentFiles,
} from "utils/attachments/attachmentFileTypes";
import { ReceiptReviewDetails } from "components/common/receipts/ReceiptReviewDetails";

/**
 * A per-attachment picker, filed against something in the parent's domain.
 * Only the invoice flow uses it, assigning each file to a line item.
 */
export interface AttachmentDropdownDetails {
  options: Array<{ id: string; value: string }>;
  onSelect: (selectedId: string, assetId: string) => void;
  defaultOptionText?: string;
  defaultOptionValue?: string;
}

export enum AttachmentRenderType {
  DRAWER = "drawer",
  ACCORDION = "accordion",
}

interface BaseAttachmentProps {
  drawerType: AttachmentDrawerType;
  drawerTitle?: string;
  parentDetails?: {
    parentId: string;
    parentType: ATTACHMENT_PARENT_TYPE;
    subParentId?: string;
  };
  localAssets?: LocalImageAsset[];
  onAddAssets?: (asset: LocalImageAsset[]) => void;
  onRemoveAssets?: (assetId: string) => void;
  dropdownDetails?: AttachmentDropdownDetails;
  transactionDetails?: {
    transaction: any;
    refetchTransactionData: () => void;
  };
  shouldRefetchAttachments?: boolean;
  shouldFetchAttachments?: boolean;
  /** The drop target alone, with no list: for an intake such as a bill to scan. */
  dropZoneOnly?: boolean;
  /** The files with no add tile and no Remove, for a viewer who cannot change them. */
  readOnly?: boolean;
  /** Rows in a drawer, thumbnails on a full page. */
  layout?: "list" | "grid";
  uploadFileToOcr?: boolean;
  /** Replaces the generic upload hint, for a surface that accepts less or caps size. */
  dragAndDropHint?: React.ReactNode;
  onOcrResult?: (result: OcrInvoiceResult) => void;
  /**
   * Replaces the list with the caller's own drop target. Its `onFiles` runs the
   * same add and OCR pipeline, and `scanningFile` is the file OCR is reading.
   */
  renderDropZone?: (
    onFiles: (files: FileList) => Promise<void>,
    scanningFile: File | null,
  ) => React.ReactNode;
}

interface DrawerAttachmentProps extends BaseAttachmentProps {
  renderType: AttachmentRenderType.DRAWER;
  openAttachmentDrawer: boolean;
  handleCloseAttachmentDrawer: () => void;
}

interface AccordionAttachmentProps extends BaseAttachmentProps {
  renderType: AttachmentRenderType.ACCORDION;
}

type AttachmentDrawerProps = DrawerAttachmentProps | AccordionAttachmentProps;

const AttachmentsComponent = (props: AttachmentDrawerProps) => {
  const {
    renderType,
    drawerType,
    drawerTitle = "Attachments",
    parentDetails,
    localAssets,
    onAddAssets,
    onRemoveAssets,
    dropdownDetails,
    transactionDetails,
    shouldRefetchAttachments = true,
    shouldFetchAttachments = true,
    dropZoneOnly = false,
    readOnly = false,
    layout = "list",
    uploadFileToOcr = false,
    dragAndDropHint,
    onOcrResult,
    renderDropZone,
  } = props;

  const { t } = useTranslation();

  const {
    handleAddAttachments,
    handleRemoveAttachments,
    scanFileWithOcr,
    attachments,
    uploadingAttachments,
    isRemovingAttachment,
    isFetchingAttachments,
    ocrLoading,
  } = useAttachmentAndAssetHandling({
    ...(parentDetails && {
      parentDetails: {
        parentId: parentDetails.parentId,
        parentType: parentDetails.parentType,
        subParentId: parentDetails.subParentId,
      },
    }),
    onAddAssets,
    onRemoveAssets,
    shouldRefetchAttachments,
    shouldFetchAttachments,
  });

  // `attachments` arrives from an untyped GraphQL edge map, so the annotation
  // is the boundary: without it the asset shape is `any` all the way down.
  const displayAttachments: LocalImageAsset[] = localAssets || attachments;
  const attachmentLabels = useAttachmentListLabels();
  const viewerLabels = useAttachmentViewerLabels();
  const { printAsset, downloadAsset } = useDisplayAttachments({
    allAttachments: displayAttachments,
  });
  const attachmentFiles = [
    ...toAttachmentItems(displayAttachments, t("Attachment")),
    ...toAttachmentItems(uploadingAttachments, t("Attachment")).map((item) => ({
      ...item,
      pending: t("Uploading"),
    })),
  ];
  const [scanningFile, setScanningFile] = useState<File | null>(null);
  // The package hands the slot an AttachmentItem, which deliberately carries no
  // domain fields, so the assignment is read back from the asset by id.
  const assignedById = new Map(
    displayAttachments.map((asset) => [asset.id, asset.subParentId]),
  );
  const dropdownOptions = dropdownDetails
    ? [
        ...(dropdownDetails.defaultOptionText &&
        dropdownDetails.defaultOptionValue
          ? [
              {
                id: dropdownDetails.defaultOptionValue,
                label: dropdownDetails.defaultOptionText,
              },
            ]
          : []),
        ...dropdownDetails.options.map((option) => ({
          id: option.id,
          label: option.value,
        })),
      ]
    : [];

  const handleOcrUpload = async (input: FileList | File) => {
    let file: File | undefined;
    if (input instanceof FileList) {
      if (input.length === 0) return;
      file = input[0];
    } else if (input instanceof File) {
      file = input;
    }
    if (file) {
      setScanningFile(file);
      try {
        const result = await scanFileWithOcr(file);
        if (onOcrResult) onOcrResult(result);
      } finally {
        setScanningFile(null);
      }
    }
  };

  const handleDropFiles = async (files: FileList) => {
    await handleAddAttachments(files);
    uploadFileToOcr && (await handleOcrUpload(files));
  };

  // `accept` only filters the click picker; a dragged file arrives whatever its type.
  const handlePickedFiles = (picked: File[]) => {
    const { supported, rejected } = partitionAttachmentFiles(picked);
    if (rejected.length > 0) {
      toast.error(
        rejected.length > 1
          ? t("UnsupportedFileTypesMessage", {
              files: rejected.join(", "),
            })
          : t("UnsupportedFileTypeMessage", { file: rejected[0] }),
      );
    }
    if (supported.length === 0) return;
    handleDropFiles(toFileList(supported));
  };

  const hint = dragAndDropHint ?? t("AttachmentUploadHint");

  const renderContent = () => {
    // A caller's own list shows the scan on the file itself.
    if (ocrLoading && !renderDropZone) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "var(--space-2)",
            height: "100%",
            width: "100%",
            paddingTop: "var(--space-2)",
          }}
        >
          <CircularProgress size={24} />
          <Box component="span" sx={{ paddingLeft: "var(--space-2)" }}>
            {t("ScanningFile")}
          </Box>
        </Box>
      );
    }

    if (renderDropZone) return renderDropZone(handleDropFiles, scanningFile);

    if (dropZoneOnly) {
      return (
        <FileUpload
          variant="dropzone"
          multiple
          onFiles={handlePickedFiles}
          accept={ATTACHMENT_FILE_ACCEPT}
          hint={hint}
          height={98}
        />
      );
    }

    if (drawerType === AttachmentDrawerType.TRANSACTION && transactionDetails) {
      return (
        <ReceiptReviewDetails
          transaction={transactionDetails.transaction}
          refetchTransactionData={transactionDetails.refetchTransactionData}
          forceHideLostReceiptButton={true}
        />
      );
    }

    if (readOnly && attachmentFiles.length === 0) {
      return isFetchingAttachments ? null : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 64,
            color: "var(--color-text-secondary)",
          }}
        >
          {t("No files found")}
        </Box>
      );
    }

    return (
      <AttachmentList
        // The line-item picker needs a row; a tile has no room for it.
        layout={dropdownDetails ? "list" : layout}
        files={attachmentFiles}
        accept={ATTACHMENT_FILE_ACCEPT}
        hint={hint}
        labels={attachmentLabels}
        disabled={isRemovingAttachment}
        viewer={{ labels: viewerLabels, filmstrip: true }}
        onAdd={readOnly ? undefined : handlePickedFiles}
        onRemove={readOnly ? undefined : handleRemoveAttachments}
        onDownload={(item) => item.url && downloadAsset(item.url)}
        onPrint={(item) => item.url && printAsset(item.url)}
        {...(dropdownDetails
          ? {
              renderItemTrailing: (item) => (
                <Select
                  // The old list gave this a fixed w-48; without a
                  // width the row's trailing region is flexShrink: 0,
                  // so the field collapses to its content and the
                  // chosen line item truncates to "At...".
                  width={192}
                  options={dropdownOptions}
                  value={assignedById.get(item.id) ?? null}
                  onChange={(id) => id && dropdownDetails.onSelect(id, item.id)}
                  clearable={false}
                />
              ),
            }
          : {})}
      />
    );
  };

  if (renderType === AttachmentRenderType.ACCORDION) {
    return <>{renderContent()}</>;
  }

  if (renderType === AttachmentRenderType.DRAWER) {
    const { openAttachmentDrawer, handleCloseAttachmentDrawer } = props;
    return (
      <Drawer
        open={openAttachmentDrawer}
        onClose={handleCloseAttachmentDrawer}
        title={t(drawerTitle)}
        width="544px"
        loading={isRemovingAttachment}
        footer={
          <Button
            variant="primary-filled"
            onClick={handleCloseAttachmentDrawer}
          >
            {t("Back")}
          </Button>
        }
      >
        {renderContent()}
      </Drawer>
    );
  }

  return null;
};

export default AttachmentsComponent;
